import { ExclamationMarkTriangleFilled } from "components/icons/generated";
import IconWithTooltip from "components/IconWithTooltip";
import { RunContext } from "components/Run/Context";
import { RunElementsProvider } from "components/Run/RunElementsContext";
import PageLoading from "components/loading/PageLoading";
import Box from "ds/components/Box";
import PageInfoHeader from "ds/components/PageInfoHeader";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import { ObservabilityProcessingStatus } from "types/generated";

import NavigationBar from "../../elements/NavigationBar";
import ExportTracesButton from "../../elements/ExportTraces/Button";
import RunPageWrapper from "../../elements/RunPageWrapper";
import RunObservabilityEmptyState from "./EmptyState";
import RunObservabilityFindings from "./Findings";
import RunObservabilityPhases from "./Phases";
import RunObservabilityStatTiles from "./StatTiles";
import { useRunObservabilityData } from "./useRunObservabilityData";
import { getRunObservabilityAttentionCaption } from "./utils";

const RunObservability = () => {
  const { run } = useTypedContext(RunContext);

  const metricsStatus = run.observabilityStatus?.metrics;
  const isAvailable = metricsStatus === ObservabilityProcessingStatus.Available;

  const { workerMetrics, outcome, findings, loading, error } = useRunObservabilityData();

  const ErrorContent = useErrorHandle(error);

  const comparison = isAvailable ? workerMetrics?.comparison : undefined;
  const hasNoComparison = !!comparison && comparison.runCount < comparison.requiredRunCount;

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <PageInfoHeader
        title={
          <Box align="center" gap="medium">
            <PageInfoHeaderTitle title="Observability" />
            {hasNoComparison && (
              <IconWithTooltip
                icon={ExclamationMarkTriangleFilled}
                color="warning"
                tooltip={`No comparison with previous runs. Phase timings for this run are shown. To judge them, this page needs ${comparison.requiredRunCount} finished tracked runs that applied in the ${comparison.windowDays} days before this run, and found ${comparison.runCount}. Without them it can't say whether the run was slower than usual.`}
              />
            )}
          </Box>
        }
      >
        <ExportTracesButton />
      </PageInfoHeader>

      <RunElementsProvider>
        {metricsStatus && !isAvailable && <RunObservabilityEmptyState status={metricsStatus} />}

        {isAvailable && (
          <>
            {loading && !workerMetrics && <PageLoading />}

            {!loading && !workerMetrics && (
              <RunObservabilityEmptyState status={ObservabilityProcessingStatus.Unavailable} />
            )}

            {workerMetrics && (
              <>
                <RunPageWrapper withNavigationBar>
                  <RunObservabilityStatTiles
                    workerMetrics={workerMetrics}
                    findings={findings}
                    outcome={outcome}
                  />
                  <RunObservabilityFindings
                    key={run.id}
                    findings={findings}
                    attentionCaption={getRunObservabilityAttentionCaption(
                      workerMetrics,
                      outcome,
                      run.state
                    )}
                  />
                  <RunObservabilityPhases key={run.id} workerMetrics={workerMetrics} />
                </RunPageWrapper>
              </>
            )}
          </>
        )}

        <NavigationBar next={run.next?.id} previous={run.previous?.id} route="observability" />
      </RunElementsProvider>
    </>
  );
};

export default RunObservability;
